import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ExerciseResponse,
  IUnit,
  IUnitCompleted,
  umbracoApi,
  useGetExercisesByExerciseIdAnswersQuery,
  useGetExercisesByExerciseIdUnitsCurrentQuery,
  useGetExercisesByIdQuery,
  usePostActivitiesByFeatureIdMutation,
  usePostExercisesByExerciseIdUnitsAndIdMutation,
  usePostExercisesByExerciseIdUnitsAndIdSkipMutation,
} from "../store/api/umbraco-api";
import { UnitContentResponse, UnitContext } from "./unit-context";
import { sessionTracker } from "../session-tracker/session-tracker";

interface ExerciseContextValue extends UnitContext {
  isLoading: boolean;
  isError: boolean;
  exercise: ExerciseResponse | undefined;
  units: {
    id: string;
    unitId: number;
    name: string;
    isCorrect: boolean | undefined;
  }[];
  currentUnit: (IUnit & { title: string }) | null;
  currentIndex: number | null;
  moveForward: () => void;
  moveBack: () => void;
  moveToIndex: (index: number) => void;
  moveNext: () => void;
}

export const ExerciseContext = createContext<ExerciseContextValue | null>(null);

interface ExerciseProviderProps {
  exerciseId: number;
}

export const ExerciseProvider = ({
  exerciseId,
  children,
}: PropsWithChildren<ExerciseProviderProps>) => {
  // define api requests
  const getExerciseQuery = useGetExercisesByIdQuery({ id: exerciseId });
  const getExerciseSidebarQuery = useGetExercisesByExerciseIdAnswersQuery({
    exerciseId,
  });
  const getCurrentUnitQuery = useGetExercisesByExerciseIdUnitsCurrentQuery({
    exerciseId,
  });
  // lazy requests
  const [fetchUnitContent, unitContentState] =
    umbracoApi.useLazyGetExercisesByExerciseIdUnitsAndUnitIdQuery();
  const [postUnitCompletion, unitCompletionState] =
    usePostExercisesByExerciseIdUnitsAndIdMutation();
  const [postSkip, skipState] =
    usePostExercisesByExerciseIdUnitsAndIdSkipMutation();

  const [postActivity, activityState] = usePostActivitiesByFeatureIdMutation();

  // provider private state
  // exercise:
  const [exerciseStatus, setExerciseStatus] = useState<
    "loading" | "error" | "inProgress" | "finished"
  >("loading");
  // unit:
  const [unitStatus, setUnitStatus] = useState<
    "loading" | "error" | "question" | "answer"
  >("loading");
  const [unitIndex, setUnitIndex] = useState<number | null>(null); // null implies the unit is the current unit

  // * Derived state
  const exercise = useMemo(() => getExerciseQuery.data, [getExerciseQuery]);
  const units = useMemo(
    () =>
      getExerciseSidebarQuery.data?.map((unit) => {
        const status = unit.status as unknown as number;
        const isCorrect =
          status === 1 ? true : status === 2 ? false : undefined;
        return {
          id: unit.id || "",
          unitId: unit.unitId || 0,
          name: unit.name || "",
          isCorrect,
        };
      }) || [],
    [getExerciseSidebarQuery]
  );

  const unitContent = useMemo<UnitContentResponse>(() => {
    if (unitIndex !== null) {
      return {
        ...unitContentState.data,
        isChat: false,
        isRevealSolution: exercise?.isRevealSolution || false,
      };
    } else {
      return {
        unit: getCurrentUnitQuery.data as IUnit,
        isCompleted: unitCompletionState.isSuccess,
        completion: unitCompletionState.isSuccess
          ? unitCompletionState.originalArgs?.iUnitCompleted
          : undefined,
        isChat: false,
        isRevealSolution: exercise?.isRevealSolution || false,
      };
    }
  }, [unitIndex, unitContentState, getCurrentUnitQuery, unitCompletionState]);

  // effects
  useEffect(() => {
    if (!getExerciseQuery.isLoading && !getExerciseSidebarQuery.isLoading) {
      // Session State Debug
      console.group("🔄 Exercise Session State");
      console.log("📋 Exercise Info:", {
        exerciseId: exerciseId,
        exerciseName: exercise?.name,
        exerciseStatus: exercise?.status,
        totalUnits: exercise?.totalUnits,
        completedUnits: exercise?.completedUnits,
        finishMessage: exercise?.finishMessage
      });
      
      if (exercise?.status === 'Completed') {
        console.warn("⚠️ Exercise is Completed - new completions may be rejected");
      }
      
      console.groupEnd();
      
      setExerciseStatus("inProgress");
    }
  }, [getExerciseQuery, getExerciseSidebarQuery, exercise]);

  useEffect(() => {
    if (getCurrentUnitQuery.isSuccess) {
      if (getCurrentUnitQuery.data) {
        // ✅ Enhanced Learning Minutes: Start session tracking for current unit  
        sessionTracker.startSession(getCurrentUnitQuery.data.id || 0, exerciseId);
        setUnitStatus("question");
      } else {
        setExerciseStatus("finished");
        setUnitStatus("answer");
      }
    }
  }, [getCurrentUnitQuery, exerciseId]);

  // handlers
  const moveForward = async () => {
    // if index is not null move forward
    if (unitIndex !== null && unitIndex + 1 < units.length) {
      await moveToIndex(unitIndex + 1);
    } else {
      setUnitIndex(null);
    }
  };
  const moveBack = async () => {
    const nextIndex = unitIndex === null ? units.length - 1 : unitIndex - 1;
    await moveToIndex(nextIndex);
  };
  const moveToIndex = async (index: number) => {
    if (index < 0 || index >= units.length) return;

    setUnitStatus("loading");
    const nextUnit = units[index];
    if (nextUnit?.unitId === undefined) return;

    // ✅ Enhanced Learning Minutes: Start session tracking for new unit
    sessionTracker.startSession(nextUnit.unitId, exerciseId);

    // Original activity tracking logic (for navigation between units)
    if (
      !activityState.isLoading &&
      !activityState.isSuccess &&
      unitContentState.fulfilledTimeStamp
    ) {
      const activityData = {
        unitId: nextUnit.unitId,
        startTime: new Date(unitContentState.fulfilledTimeStamp).toISOString(),
        endTime: new Date().toISOString(),
      };

      console.group("📊 Unit Activity Tracking - Exercise (Navigation)");
      console.log("🎯 Activity Data:", {
        featureId: exerciseId,
        unitId: activityData.unitId,
        startTime: activityData.startTime,
        endTime: activityData.endTime,
        duration: new Date(activityData.endTime).getTime() - new Date(activityData.startTime).getTime(),
        durationMinutes: ((new Date(activityData.endTime).getTime() - new Date(activityData.startTime).getTime()) / 1000 / 60).toFixed(2)
      });

      // Validation checks
      if (activityData.startTime >= activityData.endTime) {
        console.error("❌ INVALID ACTIVITY TIME: Start time >= End time");
      }
      const activityDurationHours = (new Date(activityData.endTime).getTime() - new Date(activityData.startTime).getTime()) / 1000 / 60 / 60;
      if (activityDurationHours > 8) {
        console.error("❌ INVALID ACTIVITY TIME: Duration > 8 hours:", activityDurationHours.toFixed(2));
      }

      console.log("📤 Sending activity request to:", `/activities/${exerciseId}`);

      postActivity({
        featureId: exerciseId,
        createUnitActivityRequest: activityData,
      }).then((response) => {
        console.log("📥 Activity Response:", response);
        console.log("✅ Activity tracking successful");
        activityState.reset();
        console.groupEnd();
      }).catch((error) => {
        console.error("🚨 Activity tracking error:", error);
        console.groupEnd();
      });
    }

    await fetchUnitContent({
      exerciseId,
      unitId: nextUnit.unitId,
      answerId: nextUnit.id,
    });
    setUnitIndex(index);
    setUnitStatus(unitContent.isCompleted ? "answer" : "question");
  };
  const moveNext = async () => {
    setUnitStatus("loading");
    if (unitStatus === "question" && unitContent.unit?.id) {
      await postSkip({ exerciseId, id: unitContent.unit?.id });
    }
    setUnitIndex(null);
    await getCurrentUnitQuery.refetch();

    unitCompletionState.reset();
    skipState.reset();
    if (getCurrentUnitQuery.data) {
      setUnitStatus("question");
    } else {
      setExerciseStatus("finished");
    }
  };
  const complete = async (completion: IUnitCompleted) => {
    // ✅ Enhanced Learning Minutes: Mark session as completed to avoid double counting
    sessionTracker.markSessionCompleted();

    // Add unit completion debugging
    console.group("🎯 Unit Completion Debug - Exercise");
    console.log("📊 Unit Completion Data:", {
      unitId: completion.id,
      type: completion.type,
      startTime: completion.start,
      endTime: completion.end,
      duration: completion.start && completion.end ? new Date(completion.end).getTime() - new Date(completion.start).getTime() : null,
      durationMinutes: completion.start && completion.end ? ((new Date(completion.end).getTime() - new Date(completion.start).getTime()) / 1000 / 60).toFixed(2) : null
    });

    // Validation checks (same as backend)
    if (completion.start && completion.end && completion.start >= completion.end) {
      console.error("❌ INVALID TIME: Start time >= End time");
    }
    if (completion.start === null || completion.end === null) {
      console.error("❌ INVALID TIME: Start or End is null");
    }
    if (completion.start && completion.end) {
      const durationHours = (new Date(completion.end).getTime() - new Date(completion.start).getTime()) / 1000 / 60 / 60;
      if (durationHours > 8) {
        console.error("❌ INVALID TIME: Duration > 8 hours:", durationHours.toFixed(2));
      }
    }

    console.log("📤 Sending request to:", `/exercises/${exerciseId}/units/${completion.id}`);
    console.log("🔄 Enhanced learning minutes: Session marked as completed - no duplicate watch activity");

    await postUnitCompletion({
      exerciseId,
      id: completion.id || 0,
      iUnitCompleted: completion,
    }).then((response) => {
      console.log("📥 Response received:", response);
      
      if (response.error) {
        const error = response.error as any;
        console.error("❌ API Error:", error);
        if (error.status === 400) {
          console.error("❌ BAD REQUEST (400): Backend validation failed");
          console.log("🔍 Check time data validation above");
        }
      } else {
        console.log("✅ SUCCESS: Unit completion accepted");
        console.log("📋 Response Data:", response.data);
      }
      
      setUnitStatus("answer");
      getExerciseQuery.refetch();
      getExerciseSidebarQuery.refetch();
      console.groupEnd();
    }).catch((error) => {
      console.error("🚨 Network Error:", error);
      console.groupEnd();
    });
  };

  return (
    <ExerciseContext.Provider
      value={{
        isLoading: exerciseStatus === "loading",
        isError: exerciseStatus === "error",
        exercise,
        units,
        currentIndex: unitIndex,
        currentUnit:
          (getCurrentUnitQuery.data as IUnit & { title: string }) || null,
        moveForward,
        moveBack,
        moveToIndex,
        moveNext,
        unitContent: {
          isLoading: unitStatus === "loading",
          isError: unitStatus === "error",
          data: unitContent,
        },
        unitCompletion: {
          isLoading: unitCompletionState.isLoading,
          post: complete,
          response: unitCompletionState.data,
        },
      }}
    >
      {children}
    </ExerciseContext.Provider>
  );
};
